'use client';

import { useCallback, useMemo, useState } from 'react';
import {
  AlignLeft,
  ArrowDown,
  ArrowUp,
  Briefcase,
  Calendar,
  CalendarClock,
  CheckSquare,
  ChevronDown,
  Circle,
  CornerDownRight,
  FileText,
  FolderPlus,
  GraduationCap,
  Hash,
  Heading,
  Link,
  ListChecks,
  Mail,
  Phone,
  Plus,
  Sparkles,
  SquareStack,
  Trash2,
  Type,
  Upload,
  Video,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { APPLICATION_FIELD_TYPES } from '../../constants/audition.constants';
import type {
  ApplicationFieldConfig,
  ApplicationFieldType,
  ApplicationStructure,
} from '../../types/audition.types';

interface FormBuilderPanelProps {
  structure: ApplicationStructure;
  onChange: (structure: ApplicationStructure) => void;
  onFieldSelect?: (field: ApplicationFieldConfig | null) => void;
  selectedFieldKey?: string | null;
}

const FIELD_ICONS: Record<ApplicationFieldType, React.ReactNode> = {
  text: <Type className="h-4 w-4" />,
  textarea: <AlignLeft className="h-4 w-4" />,
  number: <Hash className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  phone: <Phone className="h-4 w-4" />,
  date: <Calendar className="h-4 w-4" />,
  url: <Link className="h-4 w-4" />,
  select: <ChevronDown className="h-4 w-4" />,
  multiselect: <ListChecks className="h-4 w-4" />,
  checkbox: <CheckSquare className="h-4 w-4" />,
  checkboxGroup: <SquareStack className="h-4 w-4" />,
  radio: <Circle className="h-4 w-4" />,
  file: <Upload className="h-4 w-4" />,
  video: <Video className="h-4 w-4" />,
  videoCategories: <Video className="h-4 w-4" />,
  experience: <Briefcase className="h-4 w-4" />,
  competition_styles: <Sparkles className="h-4 w-4" />,
  age_category: <CalendarClock className="h-4 w-4" />,
  institution_preferences: <GraduationCap className="h-4 w-4" />,
  heading: <Heading className="h-4 w-4" />,
  paragraph: <FileText className="h-4 w-4" />,
};

/**
 * A step (group) in the builder canvas. `heading` is the `heading`-type field
 * that acts as the step boundary in the flat `fields` list (null for an
 * implicit leading step with no heading yet). `fields` are the non-heading
 * fields that belong to this step.
 */
interface BuilderBlock {
  heading: ApplicationFieldConfig | null;
  fields: ApplicationFieldConfig[];
}

/** Split the flat field list into step blocks at each `heading` field. */
function splitIntoBlocks(fields: ApplicationFieldConfig[]): BuilderBlock[] {
  const sorted = [...fields].sort((a, b) => a.sortOrder - b.sortOrder);
  const blocks: BuilderBlock[] = [];
  let current: BuilderBlock | null = null;

  for (const field of sorted) {
    if (field.type === 'heading') {
      if (current) blocks.push(current);
      current = { heading: field, fields: [] };
      continue;
    }
    if (!current) current = { heading: null, fields: [] };
    current.fields.push(field);
  }
  if (current) blocks.push(current);
  return blocks;
}

/** Rebuild a flat, re-sequenced field list from step blocks. */
function rebuildFields(blocks: BuilderBlock[]): ApplicationFieldConfig[] {
  const out: ApplicationFieldConfig[] = [];
  let order = 0;
  for (const block of blocks) {
    if (block.heading) out.push({ ...block.heading, sortOrder: order++ });
    for (const field of block.fields) {
      out.push({ ...field, sortOrder: order++ });
    }
  }
  return out;
}

/** Generate a key unique across every existing key (fields + headings). */
function uniqueKey(base: string, existing: Set<string>): string {
  if (!existing.has(base)) return base;
  let counter = 1;
  let key = `${base}${counter}`;
  while (existing.has(key)) {
    counter += 1;
    key = `${base}${counter}`;
  }
  return key;
}

function createNewField(
  type: ApplicationFieldType,
  existing: Set<string>,
): ApplicationFieldConfig {
  const fieldMeta = APPLICATION_FIELD_TYPES.find((f) => f.value === type);
  const field: ApplicationFieldConfig = {
    key: uniqueKey(type as string, existing),
    type,
    label: fieldMeta?.label || type,
    sortOrder: 0, // re-sequenced by rebuildFields
  };
  if (['select', 'multiselect', 'checkboxGroup', 'radio'].includes(type)) {
    field.options = [
      { value: 'option1', label: 'Option 1' },
      { value: 'option2', label: 'Option 2' },
    ];
  }
  return field;
}

function AddFieldMenu({
  onAdd,
  trigger,
}: {
  onAdd: (type: ApplicationFieldType) => void;
  trigger: React.ReactNode;
}) {
  const { t } = useTranslation();
  const byCategory = (category: string) =>
    APPLICATION_FIELD_TYPES.filter((f) => f.category === category);
  const sections: { label: string; category: string }[] = [
    {
      label: t('auditions:builderPanel.sections.input'),
      category: 'input',
    },
    {
      label: t('auditions:builderPanel.sections.selection'),
      category: 'selection',
    },
    {
      label: t('auditions:builderPanel.sections.media'),
      category: 'media',
    },
    {
      label: t('auditions:builderPanel.sections.display'),
      category: 'display',
    },
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {sections.map((section, i) => (
          <div key={section.category}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {section.label}
            </DropdownMenuLabel>
            {byCategory(section.category)
              // `heading` is added via "Add step", not as a field
              .filter((f) => f.value !== 'heading')
              .map((field) => (
                <DropdownMenuItem
                  key={field.value}
                  onClick={() => onAdd(field.value)}
                >
                  {FIELD_ICONS[field.value]}
                  <span className="ml-2">{field.label}</span>
                </DropdownMenuItem>
              ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Step-based form builder canvas. Renders the flat `fields` list grouped into
 * steps (split at `heading` fields), and offers step CRUD (add / rename /
 * reorder / delete) plus per-step field CRUD (add / delete / reorder /
 * move-to-step). All mutations rebuild the flat `fields` array, so the
 * structure stays backward-compatible with the flat renderer/preview.
 */
export function FormBuilderPanel({
  structure,
  onChange,
  onFieldSelect,
  selectedFieldKey,
}: FormBuilderPanelProps) {
  const { t } = useTranslation();
  const [renamingIndex, setRenamingIndex] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const blocks = useMemo(
    () => splitIntoBlocks(structure.fields),
    [structure.fields],
  );

  const emit = useCallback(
    (nextBlocks: BuilderBlock[]) => {
      onChange({ ...structure, fields: rebuildFields(nextBlocks) });
    },
    [structure, onChange],
  );

  const allKeys = useMemo(
    () => new Set(structure.fields.map((f) => f.key)),
    [structure.fields],
  );

  // ── Step operations ────────────────────────────────────────────────
  const handleAddStep = useCallback(() => {
    const key = uniqueKey('step', allKeys);
    const heading: ApplicationFieldConfig = {
      key,
      type: 'heading',
      label: 'New Step',
      sortOrder: 0,
    };
    emit([...blocks, { heading, fields: [] }]);
  }, [blocks, allKeys, emit]);

  const handleRenameStep = useCallback(
    (index: number, name: string) => {
      const next = blocks.map((block, i) => {
        if (i !== index) return block;
        if (block.heading) {
          return { ...block, heading: { ...block.heading, label: name } };
        }
        // implicit leading step gets a real heading on first rename
        return {
          ...block,
          heading: {
            key: uniqueKey('step', allKeys),
            type: 'heading' as const,
            label: name,
            sortOrder: 0,
          },
        };
      });
      emit(next);
    },
    [blocks, allKeys, emit],
  );

  const handleMoveStep = useCallback(
    (index: number, dir: -1 | 1) => {
      const target = index + dir;
      if (target < 0 || target >= blocks.length) return;
      const next = [...blocks];
      const [moved] = next.splice(index, 1);
      if (!moved) return;
      next.splice(target, 0, moved);
      emit(next);
    },
    [blocks, emit],
  );

  const handleDeleteStep = useCallback(
    (index: number) => {
      const block = blocks[index];
      if (!block || block.fields.length > 0) return; // empty-only delete
      emit(blocks.filter((_, i) => i !== index));
    },
    [blocks, emit],
  );

  // ── Field operations ───────────────────────────────────────────────
  const handleAddField = useCallback(
    (blockIndex: number, type: ApplicationFieldType) => {
      const field = createNewField(type, allKeys);
      const next = blocks.map((block, i) =>
        i === blockIndex
          ? { ...block, fields: [...block.fields, field] }
          : block,
      );
      emit(next);
      onFieldSelect?.(field);
    },
    [blocks, allKeys, emit, onFieldSelect],
  );

  const handleDeleteField = useCallback(
    (key: string) => {
      // Locked (system-enforced) fields cannot be removed.
      const isLocked = structure.fields.some((f) => f.key === key && f.locked);
      if (isLocked) return;
      const next = blocks.map((block) => ({
        ...block,
        fields: block.fields.filter((f) => f.key !== key),
      }));
      emit(next);
      if (selectedFieldKey === key) onFieldSelect?.(null);
    },
    [blocks, emit, selectedFieldKey, onFieldSelect, structure.fields],
  );

  const handleMoveField = useCallback(
    (blockIndex: number, fieldIndex: number, dir: -1 | 1) => {
      const block = blocks[blockIndex];
      if (!block) return;
      const target = fieldIndex + dir;
      if (target < 0 || target >= block.fields.length) return;
      const fields = [...block.fields];
      const [moved] = fields.splice(fieldIndex, 1);
      if (!moved) return;
      fields.splice(target, 0, moved);
      emit(blocks.map((b, i) => (i === blockIndex ? { ...b, fields } : b)));
    },
    [blocks, emit],
  );

  const handleMoveFieldToStep = useCallback(
    (fromIndex: number, fieldKey: string, toIndex: number) => {
      if (fromIndex === toIndex) return;
      const from = blocks[fromIndex];
      const field = from?.fields.find((f) => f.key === fieldKey);
      if (!field) return;
      const next = blocks.map((block, i) => {
        if (i === fromIndex) {
          return {
            ...block,
            fields: block.fields.filter((f) => f.key !== fieldKey),
          };
        }
        if (i === toIndex) {
          return { ...block, fields: [...block.fields, field] };
        }
        return block;
      });
      emit(next);
    },
    [blocks, emit],
  );

  const stepLabel = (block: BuilderBlock, index: number) =>
    block.heading?.label ||
    (index === 0
      ? t('auditions:builderPanel.defaultStepFirst')
      : t('auditions:builderPanel.defaultStep', { number: index + 1 }));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">
              <Trans i18nKey="auditions:builderPanel.title" />
            </CardTitle>
            <CardDescription>
              <Trans i18nKey="auditions:builderPanel.description" />
            </CardDescription>
          </div>
          <Button size="sm" onClick={handleAddStep}>
            <FolderPlus className="mr-2 h-4 w-4" />
            <Trans i18nKey="auditions:builderPanel.addStep" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {blocks.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed p-8 text-center">
            <p className="text-muted-foreground text-sm">
              <Trans i18nKey="auditions:builderPanel.emptySteps" />
            </p>
          </div>
        ) : (
          blocks.map((block, blockIndex) => (
            <div
              key={block.heading?.key ?? `implicit-${blockIndex}`}
              className="bg-muted/20 rounded-lg border"
            >
              {/* Step header */}
              <div className="flex items-center gap-2 border-b px-3 py-2">
                <Heading className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                {renamingIndex === blockIndex ? (
                  <Input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => {
                      if (renameValue.trim()) {
                        handleRenameStep(blockIndex, renameValue.trim());
                      }
                      setRenamingIndex(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (renameValue.trim()) {
                          handleRenameStep(blockIndex, renameValue.trim());
                        }
                        setRenamingIndex(null);
                      }
                      if (e.key === 'Escape') setRenamingIndex(null);
                    }}
                    className="h-7 flex-1"
                  />
                ) : (
                  <Button
                    variant="ghost"
                    type="button"
                    className="flex-1 truncate text-left text-sm font-semibold"
                    onClick={() => {
                      setRenamingIndex(blockIndex);
                      setRenameValue(stepLabel(block, blockIndex));
                    }}
                  >
                    {stepLabel(block, blockIndex)}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={blockIndex === 0}
                  onClick={() => handleMoveStep(blockIndex, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={blockIndex === blocks.length - 1}
                  onClick={() => handleMoveStep(blockIndex, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <AddFieldMenu
                  onAdd={(type) => handleAddField(blockIndex, type)}
                  trigger={
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <Plus className="h-4 w-4" />
                    </Button>
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive h-7 w-7"
                  disabled={block.fields.length > 0}
                  title={
                    block.fields.length > 0
                      ? t('auditions:builderPanel.removeFieldsFirst')
                      : t('auditions:builderPanel.deleteStep')
                  }
                  onClick={() => handleDeleteStep(blockIndex)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Step fields */}
              <div className="space-y-2 p-3">
                {block.fields.length === 0 ? (
                  <p className="text-muted-foreground py-2 text-center text-xs">
                    <Trans i18nKey="auditions:builderPanel.emptyStepFields" />
                  </p>
                ) : (
                  block.fields.map((field, fieldIndex) => (
                    <div
                      key={field.key}
                      onClick={() => onFieldSelect?.(field)}
                      className={cn(
                        'bg-background flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors',
                        selectedFieldKey === field.key
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50',
                      )}
                    >
                      <div className="text-muted-foreground flex-shrink-0">
                        {FIELD_ICONS[field.type]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">
                            {field.label}
                          </span>
                          {field.validation?.required && (
                            <span className="text-destructive text-xs">*</span>
                          )}
                          {field.isManagementField && (
                            <span className="bg-secondary rounded px-1.5 py-0.5 text-xs">
                              <Trans i18nKey="auditions:builderPanel.adminOnly" />
                            </span>
                          )}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {APPLICATION_FIELD_TYPES.find(
                            (f) => f.value === field.type,
                          )?.label || field.type}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0"
                        disabled={fieldIndex === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField(blockIndex, fieldIndex, -1);
                        }}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0"
                        disabled={fieldIndex === block.fields.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField(blockIndex, fieldIndex, 1);
                        }}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      {blocks.length > 1 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                              title={t('auditions:builderPanel.moveToStep')}
                            >
                              <CornerDownRight className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel className="text-xs">
                              <Trans i18nKey="auditions:builderPanel.moveToStep" />
                            </DropdownMenuLabel>
                            {blocks.map((b, i) =>
                              i === blockIndex ? null : (
                                <DropdownMenuItem
                                  key={b.heading?.key ?? `b-${i}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveFieldToStep(
                                      blockIndex,
                                      field.key,
                                      i,
                                    );
                                  }}
                                >
                                  {stepLabel(b, i)}
                                </DropdownMenuItem>
                              ),
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive h-7 w-7 flex-shrink-0"
                        disabled={field.locked}
                        title={
                          field.locked
                            ? t('auditions:builderPanel.lockedFieldTooltip')
                            : t('auditions:builderPanel.deleteField')
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteField(field.key);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
